/**
 * Gift Card Service
 * Handles integration with gift card API providers (Tango Card, Giftbit, etc.)
 * 
 * SETUP REQUIRED:
 * 1. Sign up with a gift card provider (Tango Card recommended)
 * 2. Get API credentials
 * 3. Add to .env file:
 *    - VITE_GIFTCARD_API_KEY
 *    - VITE_GIFTCARD_API_SECRET
 *    - VITE_GIFTCARD_MERCHANT_ID
 *    - VITE_GIFTCARD_API_URL
 *    - VITE_GIFTCARD_ENVIRONMENT (sandbox/production)
 *    - VITE_COINS_PER_DOLLAR (default: 100)
 *    - VITE_GIFTCARD_MIN_AMOUNT (default: 5)
 *    - VITE_GIFTCARD_MAX_AMOUNT (default: 100)
 */

export interface GiftCardBrand {
  id: string;
  name: string;
  logo?: string;
  minAmount: number;
  maxAmount: number;
  available: boolean;
}

export interface GiftCardRedemption {
  userId: string;
  brandId: string;
  brandName: string;
  amount: number; // Dollar amount
  coinsUsed: number;
  recipientEmail: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  giftCardCode?: string;
  giftCardLink?: string;
  errorMessage?: string;
  createdAt: Date;
  completedAt?: Date;
}

// Popular gift card brands (configurable)
export const GIFT_CARD_BRANDS: GiftCardBrand[] = [
  { id: 'amazon', name: 'Amazon', minAmount: 5, maxAmount: 200, available: true },
  { id: 'walmart', name: 'Walmart', minAmount: 5, maxAmount: 200, available: true },
  { id: 'target', name: 'Target', minAmount: 5, maxAmount: 200, available: true },
  { id: 'starbucks', name: 'Starbucks', minAmount: 5, maxAmount: 100, available: true },
  { id: 'bestbuy', name: 'Best Buy', minAmount: 5, maxAmount: 200, available: true },
  { id: 'homedepot', name: 'Home Depot', minAmount: 5, maxAmount: 200, available: true },
  { id: 'lowes', name: "Lowe's", minAmount: 5, maxAmount: 200, available: true },
  { id: 'gamestop', name: 'GameStop', minAmount: 5, maxAmount: 100, available: true },
  { id: 'itunes', name: 'iTunes/Apple', minAmount: 10, maxAmount: 200, available: true },
  { id: 'googleplay', name: 'Google Play', minAmount: 10, maxAmount: 200, available: true },
  { id: 'netflix', name: 'Netflix', minAmount: 15, maxAmount: 100, available: true },
  { id: 'spotify', name: 'Spotify', minAmount: 10, maxAmount: 100, available: true },
];

// Configuration from environment variables
const getConfig = () => {
  const coinsPerDollar = parseInt(import.meta.env.VITE_COINS_PER_DOLLAR || '1000', 10);
  const minAmount = parseInt(import.meta.env.VITE_GIFTCARD_MIN_AMOUNT || '5', 10);
  const maxAmount = parseInt(import.meta.env.VITE_GIFTCARD_MAX_AMOUNT || '100', 10);
  const apiKey = import.meta.env.VITE_GIFTCARD_API_KEY;
  const apiSecret = import.meta.env.VITE_GIFTCARD_API_SECRET;
  const merchantId = import.meta.env.VITE_GIFTCARD_MERCHANT_ID;
  const apiUrl = import.meta.env.VITE_GIFTCARD_API_URL || 'https://api.tangocard.com';
  const environment = import.meta.env.VITE_GIFTCARD_ENVIRONMENT || 'sandbox';

  return {
    coinsPerDollar,
    minAmount,
    maxAmount,
    apiKey,
    apiSecret,
    merchantId,
    apiUrl,
    environment,
    isConfigured: !!(apiKey && apiSecret),
  };
};

/**
 * Convert coins to dollar amount
 */
export const coinsToDollars = (coins: number): number => {
  const { coinsPerDollar } = getConfig();
  return Math.floor(coins / coinsPerDollar);
};

/**
 * Convert dollar amount to coins
 */
export const dollarsToCoins = (dollars: number): number => {
  const { coinsPerDollar } = getConfig();
  return dollars * coinsPerDollar;
};

/**
 * Get available gift card brands
 */
export const getAvailableBrands = (): GiftCardBrand[] => {
  return GIFT_CARD_BRANDS.filter(brand => brand.available);
};

/**
 * Validate redemption request
 */
export const validateRedemption = (
  brandId: string,
  amount: number,
  coinsAvailable: number
): { valid: boolean; error?: string } => {
  const config = getConfig();
  const brand = GIFT_CARD_BRANDS.find(b => b.id === brandId);

  if (!brand) {
    return { valid: false, error: 'Invalid gift card brand selected' };
  }

  if (!brand.available) {
    return { valid: false, error: 'This gift card brand is currently unavailable' };
  }

  if (amount < brand.minAmount) {
    return { valid: false, error: `Minimum amount is $${brand.minAmount}` };
  }

  if (amount > brand.maxAmount) {
    return { valid: false, error: `Maximum amount is $${brand.maxAmount}` };
  }

  if (amount < config.minAmount) {
    return { valid: false, error: `Minimum redemption is $${config.minAmount}` };
  }

  if (amount > config.maxAmount) {
    return { valid: false, error: `Maximum redemption is $${config.maxAmount}` };
  }

  const coinsNeeded = dollarsToCoins(amount);
  if (coinsNeeded > coinsAvailable) {
    return { valid: false, error: 'Insufficient coins for this redemption' };
  }

  return { valid: true };
};

/**
 * Redeem coins for gift card
 * 
 * NOTE: This is a placeholder implementation. You need to:
 * 1. Sign up with a gift card API provider (Tango Card recommended)
 * 2. Get API credentials
 * 3. Implement the actual API call below
 * 
 * Example Tango Card API call:
 * POST https://api.tangocard.com/raas/v2/orders
 * Headers: Authorization: Basic {base64(apiKey:apiSecret)}
 * Body: {
 *   "accountIdentifier": "your-account-id",
 *   "amount": 10.00,
 *   "customerIdentifier": "user-email",
 *   "sendEmail": true,
 *   "utid": "amazon-us"
 * }
 */
export const redeemGiftCard = async (
  userId: string,
  brandId: string,
  amount: number,
  recipientEmail: string
): Promise<GiftCardRedemption> => {
  const config = getConfig();
  const brand = GIFT_CARD_BRANDS.find(b => b.id === brandId);

  if (!brand) {
    throw new Error('Invalid gift card brand');
  }

  const coinsNeeded = dollarsToCoins(amount);

  // Create redemption record
  const redemption: GiftCardRedemption = {
    userId,
    brandId,
    brandName: brand.name,
    amount,
    coinsUsed: coinsNeeded,
    recipientEmail,
    status: 'pending',
    createdAt: new Date(),
  };

  // Check if API is configured
  if (!config.isConfigured) {
    // In development/sandbox mode without API, simulate success
    console.warn('⚠️ Gift Card API not configured. Running in simulation mode.');
    console.log('📧 Would send gift card:', {
      brand: brand.name,
      amount: `$${amount}`,
      email: recipientEmail,
      coins: coinsNeeded,
    });

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Return simulated success
    return {
      ...redemption,
      status: 'completed',
      giftCardCode: 'SIMULATED-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
      giftCardLink: 'https://example.com/giftcard',
      completedAt: new Date(),
    };
  }

  // TODO: Implement actual API call
  // Example for Tango Card:
  try {
    const response = await fetch(`${config.apiUrl}/raas/v2/orders`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`${config.apiKey}:${config.apiSecret}`)}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountIdentifier: config.merchantId,
        amount: amount.toFixed(2),
        customerIdentifier: recipientEmail,
        sendEmail: true,
        utid: getTangoCardUtid(brandId), // Map brand ID to Tango Card UTID
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to redeem gift card');
    }

    const data = await response.json();

    return {
      ...redemption,
      status: 'completed',
      giftCardCode: data.referenceOrderID,
      giftCardLink: data.giftCard?.cardNumber ? `https://giftcards.tangocard.com/${data.giftCard.cardNumber}` : undefined,
      completedAt: new Date(),
    };
  } catch (error) {
    console.error('Gift card redemption error:', error);
    return {
      ...redemption,
      status: 'failed',
      errorMessage: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Map brand ID to Tango Card UTID (Universal Type Identifier)
 * This mapping depends on Tango Card's catalog
 */
const getTangoCardUtid = (brandId: string): string => {
  const utidMap: Record<string, string> = {
    'amazon': 'amazon-us',
    'walmart': 'walmart-us',
    'target': 'target-us',
    'starbucks': 'starbucks-us',
    'bestbuy': 'best-buy-us',
    'homedepot': 'home-depot-us',
    'lowes': 'lowes-us',
    'gamestop': 'gamestop-us',
    'itunes': 'itunes-us',
    'googleplay': 'google-play-us',
    'netflix': 'netflix-us',
    'spotify': 'spotify-us',
  };

  return utidMap[brandId] || brandId;
};

/**
 * Get redemption status
 */
export const getRedemptionStatus = async (redemptionId: string): Promise<GiftCardRedemption | null> => {
  // TODO: Implement API call to check status
  // This would query your database or the gift card provider's API
  return null;
};

